import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import PercentageInput from '@components/common/PercentageInput';
import DatePicker from '@components/common/DatePicker';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { COLORS } from '@utils/constants';

const investmentSchema = z.object({
  name: z.string().min(2, 'Nome obrigatório'),
  type: z.string().min(1, 'Tipo obrigatório'),
  institution: z.string().optional(),
  initialAmount: z.number({ invalid_type_error: 'Valor inicial inválido' }).min(0, 'Valor inicial inválido'),
  monthlyContribution: z.number().min(0).default(0),
  interestRate: z.number().min(0).max(100, 'Taxa inválida').optional().nullable(),
  interestType: z.string().default('annual'),
  cdiPercentage: z.number().min(0).max(200).optional().nullable(),
  indexer: z.string().default('pre'),
  startDate: z.string().min(1, 'Data obrigatória'),
  maturityDate: z.string().optional().nullable(),
  goalAmount: z.number().min(0).optional().nullable(),
  goalDescription: z.string().optional(),
  color: z.string().default('#6366F1'),
  icon: z.string().default('📈'),
  notes: z.string().optional(),
});

const typeOptions = [
  { value: 'cdb', label: 'CDB' },
  { value: 'lci', label: 'LCI' },
  { value: 'lca', label: 'LCA' },
  { value: 'tesouro_selic', label: 'Tesouro Selic' },
  { value: 'tesouro_ipca', label: 'Tesouro IPCA+' },
  { value: 'tesouro_prefixado', label: 'Tesouro Prefixado' },
  { value: 'poupanca', label: 'Poupança' },
  { value: 'fundo', label: 'Fundo de Investimento' },
  { value: 'debenture', label: 'Debênture' },
  { value: 'cri', label: 'CRI' },
  { value: 'cra', label: 'CRA' },
  { value: 'other', label: 'Outro' },
];

const interestTypeOptions = [
  { value: 'annual', label: 'Taxa Anual (% a.a.)' },
  { value: 'monthly', label: 'Taxa Mensal (% a.m.)' },
  { value: 'cdi_percentage', label: '% do CDI' },
];

const indexerOptions = [
  { value: 'pre', label: 'Prefixado' },
  { value: 'cdi', label: 'CDI' },
  { value: 'ipca', label: 'IPCA+' },
  { value: 'selic', label: 'Selic' },
];

const icons = ['📈', '💰', '🏦', '💵', '📊', '🎯', '💎', '🏆', '⭐', '🚀'];

const InvestmentForm = ({ investment, onSubmit, onCancel, isLoading }) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(investmentSchema),
    defaultValues: {
      name: '',
      type: 'cdb',
      institution: '',
      initialAmount: '',
      monthlyContribution: 0,
      interestRate: '',
      interestType: 'annual',
      cdiPercentage: '',
      indexer: 'pre',
      startDate: new Date().toISOString().split('T')[0],
      maturityDate: '',
      goalAmount: null,
      goalDescription: '',
      color: '#6366F1',
      icon: '📈',
      notes: '',
    },
  });

  const selectedColor = watch('color');
  const selectedIcon = watch('icon');
  const interestType = watch('interestType');
  const selectedType = watch('type');

  useEffect(() => {
    if (investment) {
      reset({
        name: investment.name,
        type: investment.type,
        institution: investment.institution || '',
        initialAmount: investment.initialAmount,
        monthlyContribution: investment.monthlyContribution || 0,
        interestRate: investment.interestRate,
        interestType: investment.interestType || 'annual',
        cdiPercentage: investment.cdiPercentage || '',
        indexer: investment.indexer || 'pre',
        startDate: investment.startDate,
        maturityDate: investment.maturityDate || '',
        goalAmount: investment.goalAmount ?? null,
        goalDescription: investment.goalDescription || '',
        color: investment.color || '#6366F1',
        icon: investment.icon || '📈',
        notes: investment.notes || '',
      });
    }
  }, [investment, reset]);

  useEffect(() => {
    if (!investment) {
      switch (selectedType) {
        case 'tesouro_selic':
          setValue('indexer', 'selic');
          setValue('interestType', 'cdi_percentage');
          break;
        case 'tesouro_ipca':
          setValue('indexer', 'ipca');
          setValue('interestType', 'annual');
          break;
        case 'tesouro_prefixado':
          setValue('indexer', 'pre');
          setValue('interestType', 'annual');
          break;
        case 'cdb':
        case 'lci':
        case 'lca':
          setValue('indexer', 'cdi');
          setValue('interestType', 'cdi_percentage');
          break;
        case 'poupanca':
          setValue('indexer', 'selic');
          break;
      }
    }
  }, [selectedType, investment, setValue]);

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      maturityDate: data.maturityDate || null,
      goalAmount: data.goalAmount || null,
      cdiPercentage: data.interestType === 'cdi_percentage' ? data.cdiPercentage : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Input
        label="Nome do Investimento"
        placeholder="Ex: CDB Banco Inter, Tesouro Selic 2029..."
        error={errors.name?.message}
        {...register('name')}
      />

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Tipo"
          options={typeOptions}
          error={errors.type?.message}
          {...register('type')}
        />

        <Input
          label="Instituição (opcional)"
          placeholder="Ex: Nubank, XP, BTG..."
          {...register('institution')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="initialAmount"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Valor Inicial"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? '')}
              onBlur={field.onBlur}
              error={errors.initialAmount?.message}
            />
          )}
        />

        <Controller
          name="monthlyContribution"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Aporte Mensal"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? 0)}
              onBlur={field.onBlur}
              hint="Deixe 0 para investimento único"
              error={errors.monthlyContribution?.message}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Tipo de Rentabilidade"
          options={interestTypeOptions}
          error={errors.interestType?.message}
          {...register('interestType')}
        />

        {interestType === 'cdi_percentage' ? (
          <Controller
            name="cdiPercentage"
            control={control}
            render={({ field }) => (
              <PercentageInput
                label="% do CDI"
                value={field.value}
                onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                onBlur={field.onBlur}
                max={200}
                placeholder="Ex: 100,00 %"
                error={errors.cdiPercentage?.message}
              />
            )}
          />
        ) : (
          <Controller
            name="interestRate"
            control={control}
            render={({ field }) => (
              <PercentageInput
                label={interestType === 'monthly' ? 'Taxa Mensal (% a.m.)' : 'Taxa Anual (% a.a.)'}
                value={field.value}
                onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                onBlur={field.onBlur}
                error={errors.interestRate?.message}
              />
            )}
          />
        )}
      </div>

      <Select
        label="Indexador"
        options={indexerOptions}
        error={errors.indexer?.message}
        {...register('indexer')}
      />

      <div className="grid grid-cols-2 gap-4">
        <DatePicker
          label="Data de Início"
          error={errors.startDate?.message}
          {...register('startDate')}
        />

        <DatePicker
          label="Vencimento (opcional)"
          {...register('maturityDate')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="goalAmount"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Meta (R$) — opcional"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? null)}
              onBlur={field.onBlur}
            />
          )}
        />

        <Input
          label="Descrição da Meta"
          placeholder="Ex: Reserva de emergência"
          {...register('goalDescription')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Ícone
        </label>
        <div className="flex flex-wrap gap-2">
          {icons.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => setValue('icon', icon)}
              className={`w-10 h-10 rounded-lg border-2 text-xl flex items-center justify-center transition-colors ${
                selectedIcon === icon
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Cor
        </label>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => setValue('color', color.value)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                selectedColor === color.value
                  ? 'border-gray-900 dark:border-white scale-110'
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      <Input
        label="Observações (opcional)"
        placeholder="Adicione notas sobre o investimento..."
        {...register('notes')}
      />

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {investment ? 'Salvar' : 'Criar Investimento'}
        </Button>
      </div>
    </form>
  );
};

export default InvestmentForm;
